import "rxjs/add/operator/toPromise";

import { Injectable } from "@angular/core";

import { Api } from "../api/api";
import { FCM, Settings } from "..";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

/**
 * Most apps have the concept of a User. This is a simple provider
 * with stubs for login/signup/etc.
 *
 * This User provider makes calls to our API at the `login` and `signup` endpoints.
 *
 * By default, it expects `login` and `signup` to return a JSON object of the shape:
 *
 * ```json
 * {
 *   status: 'success',
 *   user: {
 *     // User fields your app needs, like "id", "name", "email", etc.
 *   }
 * }Ø
 * ```
 *
 * If the `status` field is not `success`, then an error is detected and returned.
 */
@Injectable()
export class User {
  public _user: any;

  constructor(public api: Api, public settings: Settings, public fcm: FCM) {}

  /**
   * Send a POST request to our login endpoint with the data
   * the user entered on the form.
   */
  login(accountInfo: any) {
    return Observable.create((observer) => {
      let seq = this.api
        .post(environment.endPoints.auth.login, accountInfo)
        .share();

      seq.subscribe(
        (res: any) => {
          if (res.token) {
            this.settings.setValue("token", res.token).then(() => {
              this.me().subscribe((res) => {
                observer.next(res);
              });
            });
          } else {
            observer.error();
          }
        },
        (err) => {
          observer.error(err);
        }
      );
    });
  }

  me() {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.me,
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              this._loggedIn(res);

              // Get a FCM token
              this.fcm.getToken(res.id, res.topic);

              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              if (err.status == 401) {
                this.logout().subscribe(() => {
                  observer.error();
                });
              }
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getTrainings(status: "pending" | "completed", page: number = 1) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.trainings,
              { status, page },
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getTrainingDetails(trainingId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.training_detail.replace(
                ":trainingId",
                trainingId
              ),
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  finishTraining(trainingId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .post(
              environment.endPoints.users.finish_training.replace(
                ":trainingId",
                trainingId
              ),
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getVouchers(page: number) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.vouchers,
              { page },
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getAvailableVouchersForPurchase() {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.vouchers,
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getClassTypes() {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.class_types,
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  getEvents(start, end, classType) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.events,
              {
                start: start.toISOString(),
                end: end.toISOString(),
                classType: classType,
              },
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  joinEvent(eventId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .post(
              environment.endPoints.users.events.replace(":id", eventId),
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  cancelEvent(eventId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .delete(
              environment.endPoints.users.events.replace(":id", eventId),
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  joinEventWaitingList(eventId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .post(
              environment.endPoints.users.waiting_events.replace(
                ":id",
                eventId
              ),
              {},
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  cancelWaitingEvent(eventId) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .delete(
              environment.endPoints.users.waiting_events.replace(
                ":id",
                eventId
              ),
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  update(accountInfo: any) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .put(environment.endPoints.users.me, accountInfo, {
              headers: {
                Authorization: "Bearer " + token,
              },
            })
            .share();

          seq.subscribe(
            (res: any) => {
              if (res.token) {
                this.settings.setValue("token", res.token).then(() => {
                  this.me().subscribe(
                    (res) => {
                      observer.next(res);
                    },
                    (err) => {
                      console.error("ERROR", err);
                      observer.error(err);
                    }
                  );
                });
              } else {
                console.error("ERROR", "No token found in response");
                observer.error();
              }
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  password(accountInfo: any) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .post(environment.endPoints.auth.password.change, accountInfo, {
              headers: {
                Authorization: "Bearer " + token,
              },
            })
            .share();

          seq.subscribe(
            (res: any) => {
              if (res.token) {
                this.settings.setValue("token", res.token).then(() => {
                  this.me().subscribe(
                    (res) => {
                      observer.next(res);
                    },
                    (err) => {
                      console.error("ERROR", err);
                      observer.error(err);
                    }
                  );
                });
              } else {
                console.error("ERROR", "No token found in response");
                observer.error();
              }
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(
                err.error ? (err.error.length ? err.error[0] : err.error) : err
              );
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }

  requestPassword(accountInfo: any) {
    return Observable.create((observer) => {
      let seq = this.api
        .post(environment.endPoints.auth.password.request_reset, accountInfo)
        .share();

      seq.subscribe(
        (res) => {
          observer.next(res);
        },
        (err) => {
          console.error("ERROR", err);
          if (err.error) {
            if (err.error.violations) {
              observer.error(err.error.violations[0].message);
            } else {
              observer.error(err.error);
            }
          } else {
            observer.error(err);
          }
        }
      );
    });
  }

  /**
   * Log the user out, which forgets the session
   */
  logout() {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(
        this.settings.setValue("token", null)
      );

      tokenSeq.subscribe(() => {
        this._user = null;
        localStorage.removeItem("user");
        observer.next();
      });
    });
  }

  /**
   * Process a login/signup response to store user data
   */
  _loggedIn(resp) {
    this._user = resp;
    localStorage.setItem("user", JSON.stringify(resp));
  }

  getNotifications(page) {
    return Observable.create((observer) => {
      let tokenSeq = Observable.fromPromise(this.settings.getValue("token"));

      tokenSeq.subscribe((token) => {
        if (token) {
          let seq = this.api
            .get(
              environment.endPoints.users.my_notifications,
              {
                page: page || 1,
              },
              {
                headers: {
                  Authorization: "Bearer " + token,
                },
              }
            )
            .share();

          seq.subscribe(
            (res: any) => {
              // If the API returned a successful response, mark the user as logged in
              observer.next(res);
            },
            (err) => {
              console.error("ERROR", err);
              observer.error(err.error);
            }
          );
        } else {
          observer.error();
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/providers/user/user.ts